import { FlowPage } from '@monthio/design-system'
import {
	eventHubService,
	LoadingScreen,
	trackingService,
	useLanguage,
} from '@monthio/react-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PensionError } from './Components/PensionError'
import { PensionIntro } from './Components/PensionIntro'
import { PensionSuccessModal } from './Components/PensionSuccessModal'
import { usePensionModule } from './usePensionModule'

export const PensionResult = {
	ClosePopUp: 'ClosePopUp',
	ReportSentWithSuccess: 'ReportSentWithSuccess',
	NoDataDelivered: 'NoDataDelivered',
	ReportSentWithFailure: 'ReportSentWithFailure',
}

export const Pension = (): JSX.Element => {
	const { t } = useTranslation()
	const [visibility, setVisibility] = useState<'visible' | 'hidden'>('visible')

	const { config, data, finish } = usePensionModule()
	const { pensionSessionId } = data

	const pensionFormRef = useRef<HTMLFormElement>(null)
	const [error, setError] = useState(false)
	const [pensionDataFetched, setPensionDataFetched] = useState(false)
	const [canContinue, setCanContinue] = useState(false)
	const [mitIdLink, setMitIdLink] = useState<null | string>(null)
	const [demoProfile, setDemoProfile] = useState<null | string>(null)
	const [successMessageOpen, setSuccessMessageOpen] = useState(false)
	const [isFetching, setIsFetching] = useState(false)

	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const demoProfileParam = params.get('demoProfile')
		if (demoProfileParam) {
			setDemoProfile(demoProfileParam)
		}
	}, [window.location.search])

	useEffect(() => {
		const fetchLink = async () => {
			const link = await config.getMitIdLoginUrl()
			setMitIdLink(link)
		}

		if (data.shouldSkip) {
			eventHubService.onEvent({
				source: 'pensionClient',
				topic: 'moduleCompleted',
				payload: { module: 'pension', skipped: true },
			})
		}

		setIsFetching(true)
		fetchLink()
			.catch(() => setError(true))
			.finally(() => {
				setError(false)
				setIsFetching(false)
			})
	}, [])

	const lang = useLanguage()
	const langQueryString = `lang=${lang === 'da-DK' ? 'da' : 'en'}`

	const demoProfileQueryString = demoProfile
		? `&demoProfile=${demoProfile}`
		: ''

	const ssoLoginUrl =
		`${config.pensionWindowUrl}?${langQueryString}${demoProfileQueryString}`.replace(
			'{pensionSessionId}',
			pensionSessionId ?? '',
		)

	useEffect(() => {
		if (visibility === 'visible' && successMessageOpen) {
			setTimeout(() => setSuccessMessageOpen(false), 1500)
			setTimeout(() => {
				finish()
			}, 1200)
		}
	}, [visibility, successMessageOpen, finish])

	useEffect(() => {
		const receivePopUpMessage = (event: MessageEvent) => {
			if (!event.data) {
				trackingService.trackEvent('pensionPopUpMessageReceivedUnknownData')
				return
			}

			switch (event.data.action) {
				case PensionResult.ReportSentWithSuccess:
					setSuccessMessageOpen(true)
					setPensionDataFetched(true)
					setCanContinue(true)
					trackingService.trackEvent('pensionDataFetchedAndPersisted')
					break
				case PensionResult.NoDataDelivered:
					setSuccessMessageOpen(true)
					setPensionDataFetched(true)
					setCanContinue(true)
					trackingService.trackEvent('pensionDataFetchedWithNoData')
					break
				case PensionResult.ReportSentWithFailure:
					setCanContinue(false)
					setError(true)
					break
				default:
					break
			}
		}

		const updateVisibility = () => {
			setVisibility(document.hidden ? 'hidden' : 'visible')
		}

		const unsubscribe = config.registerPensionMessageListener(
			updateVisibility,
			receivePopUpMessage,
		)

		return () => {
			unsubscribe()
		}
	}, [])

	const handleOpenPopup = useCallback(() => {
		if (!config.pensionWindowUrl && mitIdLink) {
			const pensionWindow = window.open(mitIdLink, 'pinfoForm')
			pensionWindow?.focus()
		} else {
			config.openPopup(pensionFormRef)
		}
	}, [pensionSessionId, mitIdLink])

	const handleSuccessMessageClose = useCallback(
		() => setSuccessMessageOpen(false),
		[],
	)

	const buttons = [
		<FlowPage.Cta
			key="openPopupButton"
			disabled={pensionDataFetched}
			onClick={handleOpenPopup}
		>
			{error ? t('pension-popupLink-retry') : t('pension-popupLink')}
		</FlowPage.Cta>,
	]

	const isPageVisibilitySupported = document.hidden !== undefined

	if (!isPageVisibilitySupported) {
		buttons.push(
			<FlowPage.Cta
				disabled={!canContinue}
				key="continueButton"
				onClick={async () => await finish()}
			>
				{t('btn-next')}
			</FlowPage.Cta>,
		)
	}

	if (isFetching) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content $spacing="xl">
				{error ? <PensionError /> : <PensionIntro />}
			</FlowPage.Content>
			<form
				ref={pensionFormRef}
				action={ssoLoginUrl}
				method="post"
				target="pinfoForm"
			></form>
			<PensionSuccessModal
				open={successMessageOpen}
				onClose={handleSuccessMessageClose}
			/>
			<FlowPage.Footer actions={buttons} />
		</FlowPage>
	)
}
